import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, GaugeIcon, MoonIcon, MountainIcon, TimerIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Mockup standard «LØPEKLAR™ · 6 bind» (Recursos Impultienda, tienda LØPEKLAR).
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/7b6e8e6c-a05d-40f3-8995-4adc5b5ab410/1792684863588_lopeklar-mockup-caja-6-bind-cc134a48.webp';
export const BRAND = 'LØPEKLAR™';
export const DISCLAIMER = 'Ikke en offisiell utgivelse fra Norges Friidrettsforbund. Ved sykdom eller skade: rådfør deg med helsepersonell.';

export const heroGuarantees = ['Direkte nedlasting', 'Engangsbetaling', 'Ingen abonnement', '7 dager pengene-tilbake-garanti'];

export const stats = [{
  value: '6',
  label: 'Digitale bind'
}, {
  value: '240',
  label: 'Sider klare til bruk'
}, {
  value: '5',
  label: 'Bonuser inkludert'
}, {
  value: 'PDF',
  label: 'Klar til utskrift'
}, {
  value: '0→42',
  label: 'Nybegynner til maraton'
}];

export const differenceStats = [{
  value: 'Nybegynner → maraton',
  label: 'Fra løp/gå til 5 km, 10 km, halvmaraton og maraton'
}, {
  value: '6 digitale bind',
  label: 'Fra årsplan og fellesøkter til testing, soner og oppfølging'
}, {
  value: 'PDF klar til utskrift',
  label: 'Bruk på mobilen eller ta øktkortet med ut i løypa'
}];

export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Søndagskvelden går til planlegging',
  text: 'Du sitter foran PC-en søndag kveld og planlegger ukas fellestreninger – og blir likevel ikke ferdig.'
}, {
  title: 'Blandede nivåer i samme gruppe',
  text: 'Nybegynnere, mosjonister og de som jakter pers skal trene samtidig – og økta må fungere for alle.'
}, {
  title: 'Mørke, regn og glatt føre',
  text: 'Fra oktober til mars må økta passe til hodelykt, refleks og is i løypa – ikke bare til fine sommerkvelder.'
}, {
  title: 'De samme øktene om igjen',
  text: 'Uten en årsplan blir det lett «rolig tur og noen drag» hver gang, og gruppen står på stedet hvil.'
}, {
  title: 'Usikker på soner og tempo',
  text: 'Hva er egentlig terskelfart for hver løper? Å regne ut soner og tempo for hele gruppen tar tid.'
}, {
  title: 'Leting etter økter på nettet',
  text: 'Programmene du finner er laget for én løper – ikke for en gruppe på 8–40 med ulike mål.'
}];

export const afterItems = ['Et ferdig årshjul for løpegruppen – hele sesongen', 'Ukeplaner for 2 og 3 økter i uka', '12 ferdige fellesøkter minutt for minutt', 'Øktkort som kan skrives ut direkte', 'Hver økt i 3 nivåer – hele gruppen trener sammen', 'Intervaller, terskel, langtur, fartslek og bakke', 'Løpe-ABC, drills og skadeforebyggende styrke', 'Programmer fra 0 til 5 km, 10 km, halvmaraton og maraton', 'Sonetabeller I1–I5 og tempotabeller ferdig utregnet'];

export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Søndagskvelden går med til planlegging',
  text: 'Bruker du søndagskvelden på å planlegge ukas fellestreninger fra bunnen av?'
}, {
  icon: UsersIcon,
  title: 'Gruppen har blandede nivåer',
  text: 'Trenger du økter der nybegynneren og den raskeste løperen kan trene sammen – i hvert sitt tempo?'
}, {
  icon: MoonIcon,
  title: 'Mørketiden kommer',
  text: 'Trenger du trygge kvalitetsøkter med hodelykt, refleksvest og glatt føre – hele vinteren?'
}, {
  icon: GaugeIcon,
  title: 'Soner og tempo er en jungel',
  text: 'Skulle du ønske at I1–I5, terskelfart og intervalltempo var ferdig utregnet for hele gruppen?'
}, {
  icon: TimerIcon,
  title: 'Klokka er 17.55 og ingenting er klart',
  text: 'Står du på samlingsplassen med løperne på vei – uten en tydelig plan for økta?'
}, {
  icon: FolderSearchIcon,
  title: 'Du leter etter økter på nettet',
  text: 'Bruker du tid på å lete etter opplegg som passer gruppen, sesongen og mål-løpet?'
}];

// Spine colours mirror the box: navy, glacier blue, green, lime, orange, red.
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: 'Årsplan',
  title: 'Årsplan, periodisering og 12 ferdige fellesøkter',
  tagline: 'Vit nøyaktig hva hver fellestrening skal inneholde – hele sesongen.',
  intro: 'Det første bindet gir deg årshjulet for løpegruppen og tolv ferdige fellesøkter du kan bruke direkte.',
  items: ['Årshjul for 52 uker', 'Grunntrening høst og vinter', 'Spesifikk trening vår', 'Konkurranse og overgang', 'Makro-, meso- og mikrosykler', 'Ukeplaner for 2 og 3 økter', '12 økter minutt for minutt (60 min)', 'Øktkort i 3 nivåer', 'Løypeskisser', 'Sjekkliste før økt'],
  outro: 'Ferdige økter som gjør at du møter gruppen med en tydelig plan.',
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: '50 økter',
  title: 'Øktbanken: intervaller, terskel, langtur og bakke',
  tagline: 'Femti ferdige økter – velg, skriv ut, løp.',
  intro: 'Det andre bindet samler øktene du trenger gjennom året, hver med sone, varighet, pause og tre nivåer.',
  items: ['Terskel 4 × 6 min', '6 × 1000 m', '30/15-intervaller', 'Fartslek', 'Pyramide', 'Bakkeintervaller', 'Progressiv og rolig langtur', 'Sone, varighet og pause på hver økt'],
  outro: 'Variasjon som holder gruppen motivert – uten at du må finne på alt selv.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '40 drills',
  title: 'Teknikk og løpsøkonomi',
  tagline: 'Lettere steg, bedre holdning – og en plan for å rette feil.',
  intro: 'Det tredje bindet gir deg løpe-ABC og drills med illustrasjon og læringspunkter.',
  items: ['Løpe-ABC', '40 drills med illustrasjon', 'Frekvens og steglengde', 'Armføring og holdning', 'Vanlige feil og korreksjon', 'Observasjonsskjema for treneren'],
  outro: 'Teknikkarbeid som passer inn i oppvarmingen – hver uke.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: '12 uker',
  title: 'Styrke og skadeforebyggende trening for løpere',
  tagline: 'Sterkere løpere som tåler mer trening.',
  intro: 'Det fjerde bindet gir deg et 12 ukers styrkeprogram og skadeforebyggende øvelser som bidrar til å redusere risikoen for belastningsplager.',
  items: ['12 ukers styrkeprogram', 'Kroppsvekt, strikk og kettlebell', 'Mobilitet', 'Fot, legg, hofte og kjerne', 'Belastningsstyring', '«Trafikklys»-skjema for vondt og stølhet', 'Alternativ trening'],
  outro: 'Styrke som kan gjøres på samlingsplassen – uten treningssenter.',
  note: '«Trafikklys»-skjemaet er et hjelpemiddel for treneren, ikke en diagnose. Ved sykdom eller skade: rådfør deg med helsepersonell.',
  spine: 'bg-lime-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: '0 → 42 km',
  title: 'Grupper på ulike nivåer: nybegynner → maraton',
  tagline: 'Én økt, tre tempo – og et program for hvert mål.',
  intro: 'Det femte bindet viser hvordan du deler gruppen i nivåer og følger hver løper mot sitt mål-løp.',
  items: ['Nivåinndeling', '«Én økt, tre tempo»', 'Løp/gå 0 → 5 km (8 uker)', '10 km (10 uker)', 'Halvmaraton (12 uker)', 'Maraton (16 uker)', 'Vinterløping og mørke', 'Plan for løpsdagen'],
  outro: 'Alle i gruppen får en plan – fra første løpetur til maraton.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: 'I1–I5',
  title: 'Testing, soner og oppfølging',
  tagline: 'Test, regn ut sonene – og følg utviklingen.',
  intro: 'Det sjette bindet gir deg testene, de ferdig utregnede tabellene og verktøyene for å følge opp hver løper.',
  items: ['Terskeltest (30 min / 3000 m)', '5 km-test', 'Sonetabeller I1–I5 (puls og tempo)', 'Tempotabell 5 km → 10 km, halvmaraton og maraton', 'Treningslogg og progresjonsskjema', 'Evalueringssamtale', '100 ferdige tilbakemeldinger til løpere'],
  outro: 'Komplett oppfølging – uten regneark og kveldsarbeid.',
  note: `${BRAND} er et selvstendig utviklet materiell. ${DISCLAIMER}`,
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];

export const volumesIntro = {
  title: '6 digitale bind for løpetrenere',
  text: 'Et komplett system som hjelper deg å planlegge og lede fellestreningene gjennom hele løpeåret. Hvert bind har 40 sider med ferdige økter, programmer og tabeller klare til utskrift.'
};

export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Ferdige økter minutt for minutt',
  text: 'Oppvarming 15 · hoveddel 35 · nedjogg 10. Med utstyr, trenerens instruksjon og løypeskisse.'
}, {
  icon: FileTextIcon,
  title: 'Øktkort klare til utskrift',
  text: 'Hele økta på ett kort som du skriver ut eller har på mobilen – ikke mer leting i notatene.'
}, {
  icon: UsersIcon,
  title: 'Tre nivåer i hver økt',
  text: 'Samme økt, tre varianter. Nybegynnere og erfarne løpere trener sammen – i hvert sitt tempo.'
}, {
  icon: MountainIcon,
  title: 'Intervaller, terskel og bakke',
  text: 'Terskeløkter, 30/15, fartslek, pyramider, bakkedrag og langturer med sone og pause.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Tester og sonetabeller',
  text: 'Terskeltest, 5 km-test og sonetabeller I1–I5 ferdig utregnet i puls og tempo.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årshjul for løpegruppen',
  text: 'Grunntrening høst og vinter, spesifikk trening vår, konkurranse og overgang – uke for uke.'
}];

export { pageSamples } from './pageSamples';
export const pageTypes = ['Årshjul', 'Ferdige fellesøkter', 'Øktkort i 3 nivåer', 'Løypeskisser og soner'];

export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikartrener-pakken',
  text: 'Ferdige økter hjelpetreneren kan lede når du ikke selv kan være der.',
  items: ['12 ferdige økter', 'Overleveringsark', 'Tydelige instrukser', 'Klart til utskrift'],
  benefit: 'Syk eller bortreist? Treningen går likevel.'
}, {
  title: 'Oppvarming og drills på kort',
  text: 'Printbare kort med aktivering, løpe-ABC og stigningsløp – rett i lomma.',
  items: ['60 printbare kort', 'Aktivering', 'Løpe-ABC', 'Stigningsløp'],
  benefit: 'En ny oppvarming hver uke – uten forberedelse.'
}, {
  title: 'Tempotabeller og sonekalkulator',
  text: 'Ferdig utregnede tabeller fra 5 km-tid til tempo i hver sone.',
  items: ['5 km-tid → I1–I5-tempo', 'Intervalltempo', 'Prognoser 10 km, halvmaraton og maraton', 'Pace-bånd'],
  benefit: 'Riktig tempo for hver løper – på sekundet.'
}, {
  title: 'Løpsdag trinn for trinn',
  text: 'Alt du trenger når gruppen skal på løp – eller dere arrangerer klubbløp selv.',
  items: ['Sjekklister', 'Oppvarming før løp', 'Pacing-plan', 'Felles løpsdag og klubbløp'],
  benefit: 'En rolig løpsdag – for deg og løperne.'
}, {
  title: 'Kommunikasjon med løpegruppen',
  text: 'Ferdige meldinger til Spond, Facebook-gruppen eller e-post.',
  items: ['40 ferdige meldinger', 'Velkomst og ny sesong', 'Avlyst økt pga. føre', 'Mørketid og refleks'],
  benefit: 'Meldingen til gruppen skrevet på et minutt.'
}];

export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: `Kjøp ${BRAND}`,
  text: 'Gjennomfør kjøpet på et par minutter. Engangsbetaling, ingen abonnement.'
}, {
  title: 'Last ned PDF-ene på e-post',
  text: 'Du får PDF-ene tilsendt på e-post med en gang etter kjøpet.'
}, {
  title: 'Velg økta og møt gruppen',
  text: 'Finn uka i årshjulet, skriv ut øktkortet – og møt løperne med en tydelig plan.'
}];

export const audienceItems = ['Er trener i et friidrettslag, en løpegruppe eller et bedriftsidrettslag', 'Leder fellestreninger 2–3 ganger i uka', 'Har løpere på ulike nivåer i samme gruppe', 'Er frivillig trener og vil ha ferdige opplegg', 'Vil slippe å planlegge hver økt fra bunnen av', 'Følger løpere mot 5 km, 10 km, halvmaraton eller maraton', 'Vil ha trygge økter også i mørketiden', 'Vil ha søndagskvelden tilbake'];

export const timeValueTasks = ['Lete etter økter på nettet', 'Planlegge ukas fellestreninger', 'Regne ut soner og tempo', 'Lage programmer for ulike mål', 'Skrive meldinger til gruppen'];

export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Årsplan og fellesøkter, øktbank, teknikk, styrke, nivåer fra nybegynner til maraton, testing og soner'
}, {
  label: '5 bonuser uten ekstra kostnad',
  detail: 'Vikartrener-pakken, drillkort, tempotabeller, løpsdag og meldinger til løpegruppen'
}, {
  label: 'For løpetrenere',
  detail: 'Ferdige økter, øktkort i 3 nivåer, løypeskisser, sonetabeller og programmer'
}];

export const offerIncludes = ['Direkte nedlasting', 'PDF-filer på e-post', 'Klar til utskrift', 'Ingen fysisk vare sendes'];
export const PRICE = '299 kr';
export const PRICE_BEFORE = '599 kr';

// No testimonials on this page. Only authentic reviews may ever be added here.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];

export const faqs = [{
  q: 'Hvilket format har materiellet?',
  a: `${BRAND} leveres digitalt som PDF-filer. Du kan bruke dem på PC, nettbrett eller mobil og skrive ut sidene du trenger.`
}, {
  q: 'Hvordan får jeg materiellet?',
  a: 'Du får PDF-ene på e-post med en gang etter kjøpet. Ingen ventetid og ingen fysisk vare.'
}, {
  q: 'Passer det for grupper med blandede nivåer?',
  a: 'Ja. Hver fellesøkt har tre nivåer, så nybegynnere og erfarne løpere kan trene samtidig. Bind 5 viser hvordan du deler gruppen og har programmer fra løp/gå til 5 km, 10 km, halvmaraton og maraton.'
}, {
  q: 'Hva betyr sonene I1–I5?',
  a: 'Materiellet bruker en femdelt intensitetsskala (I1–I5) med pratetest og omtrentlig prosent av makspuls. Prosentene er veiledende, og sonetabellene i Bind 6 er ferdig utregnet i både puls og tempo.'
}, {
  q: 'Fungerer det i mørketiden og på vinteren?',
  a: 'Ja. Årshjulet har grunntrening høst og vinter, og du får økter for lysløype med hodelykt, sjekkliste for mørke og glatt føre og forslag til innetrening når føret er for dårlig.'
}, {
  q: 'Kan jeg skrive det ut?',
  a: 'Ja. Alt er laget klart til utskrift. Du velger selv hva du skriver ut, og du kan også bruke materiellet digitalt på mobilen ute i løypa.'
}, {
  q: 'Trenger jeg trenerkurs for å bruke det?',
  a: 'Nei. Øktene er beskrevet minutt for minutt med trenerens instruksjon, utstyr og løypeskisse. Materiellet passer både for frivillige trenere og for deg som har tatt trenerkurs.'
}, {
  q: 'Er det godkjent av Norges Friidrettsforbund?',
  a: `Nei. ${BRAND} er et selvstendig utviklet materiell. ${DISCLAIMER}`
}, {
  q: 'Kan jeg virkelig være klar til fellestrening på 5 minutter?',
  a: `${BRAND} er laget for å gjøre forberedelsene raskere. Du finner uka i årshjulet, leser gjennom øktkortet og er klar på omtrent fem minutter. Tiden kan variere etter økta, gruppen og behovet for tilpasning.`
}, {
  q: 'Er de fem bonusene inkludert?',
  a: `Ja. Alle fem bonusene er inkludert uten ekstra kostnad – alt for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nei. Du betaler én gang og får tilgang til det digitale materiellet. Ingen faste trekk.'
}, {
  q: 'Finnes det en garanti?',
  a: `Ja. ${BRAND} har 7 dager pengene-tilbake-garanti. Hvis materiellet ikke svarer til forventningene dine, kan du kontakte oss og be om å få pengene tilbake innen 7 dager etter kjøpet.`
}];

export const footerLinks = ['Kjøpsvilkår', 'Personvern', 'Refusjon', 'Kontakt'];
